import React from 'react';
import classnames from 'classnames';
import { type LeafElement } from '../types/aha-editor';
import { ApplicationView, useController } from '@aha-app/mvc';
import AhaEditorV2Controller from '../controllers/AhaEditorV2Controller';
import DecorationUtils from '../AhaEditorUtils/DecorationUtils';

export interface LeafProps {
  leaf: LeafElement;
  attributes: Record<string, unknown>;
  children?: React.ReactNode;
}

const Leaf: React.FC<LeafProps> = ({ leaf, children, attributes }) => {
  const controller = useController(AhaEditorV2Controller);
  const leafProps = controller.ahaEditor.applyMarks(leaf);

  const decorations = leaf.decorations ?? {};
  let decorationClasses = {};
  if (decorations.classNames) {
    decorationClasses = DecorationUtils.extractDecorationClasses(
      decorations.classNames
    );
  }

  const hasDecorationComponent = !!decorations.decorationComponent;

  const { inline, style, ...spanProps } = leafProps;

  if (decorations.handlers) {
    for (const [event, handler] of Object.entries(decorations.handlers)) {
      spanProps[event] = handler;
    }
  }

  // We want to set the attributes on the decorated component instead of the inner
  // span when it is present. This avoids "ghost" spaces in the rendered element.
  const spanAttributes = hasDecorationComponent ? {} : attributes;
  const leafContent = (
    <span
      {...spanAttributes}
      {...spanProps}
      className={classnames(decorationClasses, leafProps.className as string)}
      // The following is a workaround for a Chromium bug where,
      // if you have an inline at the end of a block,
      // clicking the end of a block puts the cursor inside the inline
      // instead of inside the final {text: ''} node
      // https://github.com/ianstormtaylor/slate/issues/4704#issuecomment-1006696364
      // Copied from Slate inline example.
      style={
        leaf.text === ''
          ? { paddingLeft: '0.00001px', ...(style as any) }
          : style
      }
    >
      {children}
    </span>
  );

  if (leaf.decorations?.decorationComponent) {
    const Decorated = leaf.decorations.decorationComponent;
    return (
      <span {...attributes}>
        <Decorated>{leafContent}</Decorated>
      </span>
    );
  }

  return leafContent;
};

export default ApplicationView(Leaf);
